/**
 * installPrompt — module-level capture of the `beforeinstallprompt` event.
 *
 * The browser fires `beforeinstallprompt` at a time of its own choosing
 * (often within the first seconds of the very first visit), and the event
 * can only be captured once — a caller must call `preventDefault()`
 * synchronously in the handler to defer the browser's native mini-infobar
 * and keep the event around for a later `.prompt()` call. If no listener is
 * attached at the moment the event fires, it's gone.
 *
 * Deciding WHETHER we've captured an installable event and WHEN it's polite
 * to surface our own UI are two different concerns. This module only does
 * the former — it registers the listener as a side effect at import time
 * (so it doesn't depend on which route/component happens to mount first)
 * and exposes a tiny subscribe/getSnapshot pair for `useInstallPrompt` to
 * read reactively via `useSyncExternalStore`. The "when to show" decision
 * (e.g. only after the user's first vehicle is sealed) lives entirely in
 * the `armed` prop passed to the <InstallPrompt> component — this module
 * has no opinion on it.
 *
 * Import this module as early as possible (see src/app/App.tsx) so the
 * listener is registered before the browser has a chance to fire the event.
 */

export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

let deferredEvent: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredEvent = event as BeforeInstallPromptEvent;
    notify();
  });

  // If the install completes (via our prompt, the browser's own UI, or an
  // already-installed launch), the deferred event is no longer usable.
  window.addEventListener('appinstalled', () => {
    deferredEvent = null;
    notify();
  });
}

export function getDeferredInstallEvent(): BeforeInstallPromptEvent | null {
  return deferredEvent;
}

/** Clears the deferred event after a `.prompt()` call consumes it (each event is single-use). */
export function clearDeferredInstallEvent(): void {
  deferredEvent = null;
  notify();
}

export function subscribeInstallEvent(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
